import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { calculateSecondaryPanelPosition } from 'components/pages/Planner/PlannerLineActions/calculateSecondaryPanelPosition';
import PlannerSecondaryPanel from 'components/pages/Planner/PlannerSecondaryPanel';
import { Store } from 'components/common/types/Store.types';
import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import Search from 'lib/Search';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import { hidePlannerSecondaryPanel } from 'store/dealManagement/reducer';
import { updateIsObjectiveSubsectionChange } from 'store/objective/reducer';
import { LocationProps } from './Location.types';
import LocationOptionsList from './LocationOptionsList';
import {
  transformLocationToCategories,
  transformSelectedLocationsToCodeNameModels,
  transformCodeNameModelsToSelectedLocations,
} from './utils';

const Location: React.FC<LocationProps> = ({
  locationOptions,
  selectedLocations,
  onConfirm,
  cancelButtonLabel,
  confirmButtonLabel,
  isSubsection = false,
  hideExclude,
  categoryOrder,
}) => {
  const dispatch = useDispatch();
  const isFetchingReferences = useSelector((state: Store) => state.dealManagement.isFetchingReferences);

  const categoryOptions = useMemo(() => transformLocationToCategories(locationOptions), [locationOptions]);
  const initialSelectedOptions = useMemo(
    () => transformSelectedLocationsToCodeNameModels(selectedLocations),
    [selectedLocations],
  );

  const {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    includedOptions,
    excludedOptions,
    isOptionSelected,
    isOptionIncluded,
    onOptionIncludeExcludeChange,
    onToggleCategory,
    setSelectedOptions,
    onCategoryIncludeAll,
    onCategoryExcludeAll,
    getCategoryIncludeState,
  } = useCategoryOptions({
    options: categoryOptions,
    initialSelectedOptions,
    categoryOrder,
  });

  const isChange = useMemo(
    () => !isEqual(initialSelectedOptions, selectedOptions),
    [initialSelectedOptions, selectedOptions],
  );

  useEffect(() => {
    if (isSubsection) dispatch(updateIsObjectiveSubsectionChange(isChange));
  }, [isChange, isSubsection]);

  return (
    <PlannerSecondaryPanel
      title="Location"
      onSubmit={() => onConfirm(transformCodeNameModelsToSelectedLocations(selectedOptions))}
      onCancel={() => dispatch(hidePlannerSecondaryPanel())}
      customStyles={calculateSecondaryPanelPosition({ width: 400 })}
      cancelButtonLabel={cancelButtonLabel}
      confirmButtonLabel={confirmButtonLabel}
      isDisabled={!isChange}
      isSubsection={isSubsection}
      showBackButton={isSubsection}
      isChange={isChange}
      pendoId="Location"
    >
      {isFetchingReferences ? (
        <CircularLoadingSpinner />
      ) : (
        <>
          <div className="mb-2">
            <Search value={searchText} placeholder="Search locations" onChange={onFilterTextChange} />
          </div>
          {canDisplayOptionResults(filteredOptions) && (
            <LocationOptionsList
              filteredOptions={filteredOptions}
              selectedOptions={selectedOptions}
              openCategories={openCategories}
              sortDirection={sortDirection}
              includedOptions={includedOptions}
              excludedOptions={excludedOptions}
              hideExclude={hideExclude}
              onSort={onCategoryDirectionSort}
              onToggleCategory={onToggleCategory}
              setSelectedOptions={setSelectedOptions}
              getNumberOfFilteredOptions={getNumberOfFilteredOptions}
              isOptionSelected={isOptionSelected}
              isOptionIncluded={isOptionIncluded}
              onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
              onCategoryIncludeAll={onCategoryIncludeAll}
              onCategoryExcludeAll={onCategoryExcludeAll}
              getCategoryIncludeState={getCategoryIncludeState}
            />
          )}
          {!canDisplayOptionResults(filteredOptions) && searchText.length > 0 && (
            <p>There are no results matching your search</p>
          )}
        </>
      )}
    </PlannerSecondaryPanel>
  );
};

export default Location;
